import { KnownCookies } from 'common-types';
import Head from 'next/head';
import { parseCookies } from 'nookies';
import { fetchSortConfig } from '~/api/configuration';
import { fetchFacetItems } from '~/api/items';
import { fetchSavedSearchById } from '~/api/savedSearch';
import { fetchUserSearchFilters, reduceEnabledSearchFilters } from '~/api/user';
import Layout from '~/components/Layout';
import ResultsGridBlock from '~/components/Search/ResultsGridBlock';
import SearchMetadata from '~/components/Search/SearchMetadata';
import SearchPageHeader from '~/components/Search/SearchPageHeader';
import SimpleCategoryPicker from '~/components/Search/SimpleCategoryPicker';
import { RESULTS_PAGE_SIZE } from '~/constants/resultsPageSize';
import { BrowseItemsContext } from '~/contexts/BrowseItemsContext';
import { SavedSearchContext } from '~/contexts/SavedSearchContext';
import { UserSearchFilters } from '~/contexts/UserSearchFilters';
import { FacetFilteringProvider } from '~/contexts/facetFiltering/FacetFilteringContext';
import { CustomNextPage } from '~/typings/custom-next';
import { RailsSortConfig } from '~/typings/services/rails/configuration';
import {
  FilterState,
  RailsFacet,
  RailsFacetQueryOptions,
} from '~/typings/services/rails/facets';
import { RailsThumbnailItem } from '~/typings/services/rails/item';
import { RailsPaging } from '~/typings/services/rails/paging';
import { RailsSavedSearch } from '~/typings/services/rails/savedSearch';
import { BaseUserSearchFilterResponse } from '~/typings/services/rails/userSearchFilters';
import absoluteUrl from '~/utils/absoluteUrl';

interface Props {
  facets: RailsFacet[];
  filterState: FilterState;
  items: RailsThumbnailItem[];
  paging: RailsPaging;
  savedSearch?: RailsSavedSearch;
  sorts?: RailsSortConfig;
  userSearchFilters?: BaseUserSearchFilterResponse;
}

const SearchPage: CustomNextPage<Props> = ({
  facets,
  filterState,
  items,
  paging,
  savedSearch,
  sorts,
  userSearchFilters,
}) => (
  <Layout container={false} meta={{ canonical: absoluteUrl('/search') }}>
    <Head>
      <meta name="robots" content="noindex" />
    </Head>

    <BrowseItemsContext value={{ items: items, paging: paging }}>
      <SavedSearchContext value={{ savedSearch }}>
        <UserSearchFilters value={{ filters: userSearchFilters || [] }}>
          <FacetFilteringProvider
            facets={facets}
            filterState={filterState}
            paging={paging}
            sorts={sorts}
            type="search"
          >
            <SearchMetadata />
            <SearchPageHeader />
            <SimpleCategoryPicker
              heading="Suggested Categories"
              className="mb-8"
            />
            <ResultsGridBlock />
          </FacetFilteringProvider>
        </UserSearchFilters>
      </SavedSearchContext>
    </BrowseItemsContext>
  </Layout>
);

SearchPage.getInitialProps = async ctx => {
  const query = ctx.query as RailsFacetQueryOptions;

  const cookies: KnownCookies = parseCookies(ctx);

  const [sorts, userSearchFilters, savedSearch] = await Promise.all([
    fetchSortConfig(),
    fetchUserSearchFilters.call(ctx),
    query.saved_search_id
      ? fetchSavedSearchById.call(ctx, query.saved_search_id)
      : undefined,
  ]);

  const response = await fetchFacetItems.call(ctx, {
    ...savedSearch?.query,
    ...query,
    ...reduceEnabledSearchFilters(
      userSearchFilters,
      cookies.fast_shipping_filter_enabled,
      cookies.canadian_sellers_filter_enabled,
    ),
    page_size: RESULTS_PAGE_SIZE,
    id: undefined,
    context: query.q ? 'naked_query_results' : undefined,
  });

  return {
    savedSearch,
    sorts,
    userSearchFilters,
    facets: response.meta.facets,
    filterState: { ...response.meta.query, id: Math.random() }, // id is added for Visited Results consistency
    items: response.data,
    paging: response.meta.paging,
  };
};

export default SearchPage;
